import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { GET_MY_CLIP, GET_MY_PROJECT, GET_MY_LIBRARY } from '@/queries/queries';
import type { MyClipQuery, MyClipQueryVariables } from '@/generated/graphql';
import type { MyProjectQuery, MyProjectQueryVariables, MyLibraryQuery, MyLibraryQueryVariables } from '@/generated/clip-source-pages';

// Define finished states for projects
const PROJECT_FINISHED_STATES = ['success', 'failure', 'expired'] as const;
type ProjectFinishedState = typeof PROJECT_FINISHED_STATES[number];

// Define finished states for clips
type ClipFinishedState = 'success' | 'failure';

interface UsePollingOptions {
  enabled?: boolean;
  interval?: number;
  maxAttempts?: number;
  onFinished?: (data: any) => void;
  onError?: (error: Error) => void;
}

interface UseProjectPollingReturn {
  data: MyProjectQuery['myProject'] | undefined;
  loading: boolean;
  error: Error | undefined;
  isFinished: boolean;
  finishedState: ProjectFinishedState | null;
  refetch: () => void;
}

interface UseClipPollingReturn {
  data: MyClipQuery['myClip'] | undefined;
  loading: boolean;
  error: Error | undefined;
  isFinished: boolean;
  finishedState: ClipFinishedState | null;
  refetch: () => void;
}

// Hook for polling projects
export function useProjectPolling(
  projectId: string | undefined,
  options: UsePollingOptions = {}
): UseProjectPollingReturn {
  const {
    enabled = true,
    interval = 5000, // 5 seconds
    maxAttempts = 60, // 5 minutes max
    onFinished,
    onError
  } = options;

  const [isFinished, setIsFinished] = useState(false);
  const [finishedState, setFinishedState] = useState<ProjectFinishedState | null>(null);
  const attemptCountRef = useRef(0);
  const hasCalledOnFinishedRef = useRef(false);
  const hasCalledOnErrorRef = useRef(false);

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<MyProjectQuery, MyProjectQueryVariables>(
    GET_MY_PROJECT,
    {
      variables: { id: projectId as string },
      skip: !projectId || !enabled,
      fetchPolicy: 'network-only', // Always fetch fresh data
      notifyOnNetworkStatusChange: true,
    }
  );

  const project = data?.myProject;
  const status = project?.curation_status;

  // Check if project has reached a finished state
  const hasReachedFinishedState = status && PROJECT_FINISHED_STATES.includes(status as ProjectFinishedState);

  // Determine if we should be polling
  const shouldPoll = enabled && !!projectId && !hasReachedFinishedState && !isFinished;

  // Track poll attempts
  useEffect(() => {
    if (!shouldPoll) return;

    const trackAttempts = () => {
      attemptCountRef.current++;
    };

    // Use a separate interval just for tracking attempts
    const trackingInterval = setInterval(trackAttempts, interval);

    return () => {
      clearInterval(trackingInterval);
    };
  }, [shouldPoll, interval]);

  // Start/stop Apollo's built-in polling
  useEffect(() => {
    if (shouldPoll) {
      startPolling(interval);
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [shouldPoll, interval, startPolling, stopPolling]);

  // Handle finished state
  useEffect(() => {
    if (hasReachedFinishedState && !hasCalledOnFinishedRef.current) {
      stopPolling();
      setIsFinished(true);
      setFinishedState(status as ProjectFinishedState);
      hasCalledOnFinishedRef.current = true;
      if (onFinished && project) {
        onFinished(project);
      }
    }
  }, [hasReachedFinishedState, status, project, onFinished, stopPolling]);

  // Handle max attempts exceeded
  useEffect(() => {
    if (attemptCountRef.current >= maxAttempts && !hasCalledOnErrorRef.current && !isFinished) {
      stopPolling();
      hasCalledOnErrorRef.current = true;
      if (onError) {
        onError(new Error('Max polling attempts exceeded'));
      }
    }
  }, [maxAttempts, isFinished, onError, stopPolling]);

  // Reset state when projectId or enabled changes
  useEffect(() => {
    if (!enabled || !projectId) {
      attemptCountRef.current = 0;
      hasCalledOnFinishedRef.current = false;
      hasCalledOnErrorRef.current = false;
      setIsFinished(false);
      setFinishedState(null);
    }
  }, [enabled, projectId]);

  // Memoized refetch to avoid issues
  const stableRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    data: project,
    loading,
    error,
    isFinished,
    finishedState,
    refetch: stableRefetch
  };
}

interface UseLibraryPollingReturn {
  data: MyLibraryQuery['myLibrary'] | undefined;
  loading: boolean;
  error: Error | undefined;
  isFinished: boolean;
  finishedState: ProjectFinishedState | null;
  refetch: () => void;
}

// Hook for polling libraries
export function useLibraryPolling(
  libraryId: string | undefined,
  options: UsePollingOptions = {}
): UseLibraryPollingReturn {
  const {
    enabled = true,
    interval = 5000,
    maxAttempts = 60,
    onFinished,
    onError
  } = options;

  const [isFinished, setIsFinished] = useState(false);
  const [finishedState, setFinishedState] = useState<ProjectFinishedState | null>(null);
  const attemptCountRef = useRef(0);
  const hasCalledOnFinishedRef = useRef(false);
  const hasCalledOnErrorRef = useRef(false);

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<MyLibraryQuery, MyLibraryQueryVariables>(
    GET_MY_LIBRARY,
    {
      variables: { id: libraryId as string },
      skip: !libraryId || !enabled,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    }
  );

  const library = data?.myLibrary;
  const status = library?.curation_status;

  const hasReachedFinishedState = status && PROJECT_FINISHED_STATES.includes(status as ProjectFinishedState);

  const shouldPoll = enabled && !!libraryId && !hasReachedFinishedState && !isFinished;

  useEffect(() => {
    if (!shouldPoll) return;

    const trackAttempts = () => {
      attemptCountRef.current++;
    };

    const trackingInterval = setInterval(trackAttempts, interval);

    return () => {
      clearInterval(trackingInterval);
    };
  }, [shouldPoll, interval]);

  useEffect(() => {
    if (shouldPoll) {
      startPolling(interval);
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [shouldPoll, interval, startPolling, stopPolling]);

  useEffect(() => {
    if (hasReachedFinishedState && !hasCalledOnFinishedRef.current) {
      stopPolling();
      setIsFinished(true);
      setFinishedState(status as ProjectFinishedState);
      hasCalledOnFinishedRef.current = true;
      if (onFinished && library) {
        onFinished(library);
      }
    }
  }, [hasReachedFinishedState, status, library, onFinished, stopPolling]);

  useEffect(() => {
    if (attemptCountRef.current >= maxAttempts && !hasCalledOnErrorRef.current && !isFinished) {
      stopPolling();
      hasCalledOnErrorRef.current = true;
      if (onError) {
        onError(new Error('Max polling attempts exceeded'));
      }
    }
  }, [maxAttempts, isFinished, onError, stopPolling]);

  useEffect(() => {
    if (!enabled || !libraryId) {
      attemptCountRef.current = 0;
      hasCalledOnFinishedRef.current = false;
      hasCalledOnErrorRef.current = false;
      setIsFinished(false);
      setFinishedState(null);
    }
  }, [enabled, libraryId]);

  const stableRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    data: library,
    loading,
    error,
    isFinished,
    finishedState,
    refetch: stableRefetch
  };
}

// Hook for polling clips
export function useClipPolling(
  clipId: string | undefined,
  options: UsePollingOptions = {}
): UseClipPollingReturn {
  const {
    enabled = true,
    interval = 5000, // 5 seconds
    maxAttempts = Infinity, // Infinite max attempts
    onFinished,
    onError
  } = options;

  const [isFinished, setIsFinished] = useState(false);
  const [finishedState, setFinishedState] = useState<ClipFinishedState | null>(null);
  const attemptCountRef = useRef(0);
  const hasCalledOnFinishedRef = useRef(false);
  const hasCalledOnErrorRef = useRef(false);

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<MyClipQuery, MyClipQueryVariables>(
    GET_MY_CLIP,
    {
      variables: { id: clipId as string },
      skip: !clipId || !enabled,
      fetchPolicy: 'network-only', // Always fetch fresh data
      notifyOnNetworkStatusChange: true,
    }
  );

  const clip = data?.myClip;

  // Check if all processing stages are finished
  const clippingStatus = clip?.clipping_status;
  const preparingStatus = clip?.preparing_status;
  const renderingStatus = clip?.rendering_status;

  // A clip is finished when all stages are complete (success or failure)
  const hasFailure = clip && [clippingStatus, preparingStatus, renderingStatus].includes('failure');
  const allSuccess = clip && [clippingStatus, preparingStatus, renderingStatus].every(status => status === 'success' || status === 'skipped');
  const hasReachedFinishedState = hasFailure || allSuccess;

  // Determine if we should be polling
  const shouldPoll = enabled && !!clipId && !hasReachedFinishedState && !isFinished;

  // Track poll attempts
  useEffect(() => {
    if (!shouldPoll || maxAttempts === Infinity) return;

    const trackAttempts = () => {
      attemptCountRef.current++;
    };

    // Use a separate interval just for tracking attempts
    const trackingInterval = setInterval(trackAttempts, interval);

    return () => {
      clearInterval(trackingInterval);
    };
  }, [shouldPoll, interval, maxAttempts]);

  // Start/stop Apollo's built-in polling
  useEffect(() => {
    if (shouldPoll) {
      startPolling(interval);
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [shouldPoll, interval, startPolling, stopPolling]);

  // Handle finished state
  useEffect(() => {
    if (hasReachedFinishedState && !hasCalledOnFinishedRef.current) {
      stopPolling();
      setIsFinished(true);

      if (hasFailure) {
        setFinishedState('failure');
      } else if (allSuccess) {
        setFinishedState('success');
      }

      hasCalledOnFinishedRef.current = true;
      if (onFinished && clip) {
        onFinished(clip);
      }
    }
  }, [hasReachedFinishedState, hasFailure, allSuccess, clip, onFinished, stopPolling]);

  // Handle max attempts exceeded
  useEffect(() => {
    if (maxAttempts !== Infinity && attemptCountRef.current >= maxAttempts && !hasCalledOnErrorRef.current && !isFinished) {
      stopPolling();
      hasCalledOnErrorRef.current = true;
      if (onError) {
        onError(new Error('Max polling attempts exceeded'));
      }
    }
  }, [maxAttempts, isFinished, onError, stopPolling]);

  // Reset state when clipId or enabled changes
  useEffect(() => {
    if (!enabled || !clipId) {
      attemptCountRef.current = 0;
      hasCalledOnFinishedRef.current = false;
      hasCalledOnErrorRef.current = false;
      setIsFinished(false);
      setFinishedState(null);
    }
  }, [enabled, clipId]);

  // Memoized refetch to avoid issues
  const stableRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    data: clip,
    loading,
    error,
    isFinished,
    finishedState,
    refetch: stableRefetch
  };
}
